const MAX_IMAGE_EDGE = 1600;
const TARGET_IMAGE_BYTES = 800 * 1024;
const JPEG_QUALITIES = [0.82, 0.72, 0.62] as const;
const MAX_RESIZE_PASSES = 4;
const COMPRESSIBLE_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

type ImageSize = { width: number; height: number };
type LoadedImage = ImageSize & { source: CanvasImageSource; dispose: () => void };

export const shouldCompressImage = (file: Pick<File, "size" | "type">): boolean =>
  COMPRESSIBLE_IMAGE_TYPES.has(file.type) && file.size > TARGET_IMAGE_BYTES;

export const scaledImageDimensions = (width: number, height: number, maxEdge = MAX_IMAGE_EDGE): ImageSize => {
  if (width <= 0 || height <= 0) throw new Error("Image dimensions must be positive.");
  const scale = Math.min(1, maxEdge / Math.max(width, height));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
};

export const reducedImageDimensions = (dimensions: ImageSize, currentBytes: number, targetBytes = TARGET_IMAGE_BYTES): ImageSize => {
  if (currentBytes <= 0 || targetBytes <= 0) throw new Error("Image byte sizes must be positive.");
  const scale = Math.min(0.9, Math.max(0.6, Math.sqrt(targetBytes / currentBytes) * 0.95));
  return {
    width: Math.max(1, Math.round(dimensions.width * scale)),
    height: Math.max(1, Math.round(dimensions.height * scale)),
  };
};

const loadImage = async (file: File): Promise<LoadedImage> => {
  if (typeof createImageBitmap === "function") {
    const bitmap = await createImageBitmap(file);
    return { source: bitmap, width: bitmap.width, height: bitmap.height, dispose: () => bitmap.close() };
  }

  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.decoding = "async";
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error("Could not decode the selected image."));
      image.src = url;
    });
    return { source: image, width: image.naturalWidth, height: image.naturalHeight, dispose: () => URL.revokeObjectURL(url) };
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
};

const canvasBlob = (canvas: HTMLCanvasElement, quality: number): Promise<Blob> =>
  new Promise((resolve, reject) => canvas.toBlob(
    (blob) => blob ? resolve(blob) : reject(new Error("Could not compress the selected image.")),
    "image/jpeg",
    quality
  ));

export const compressImageForUpload = async (file: File): Promise<File> => {
  if (!shouldCompressImage(file)) return file;

  try {
    const image = await loadImage(file);
    try {
      let dimensions = scaledImageDimensions(image.width, image.height);
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      if (!context) return file;

      let smallest: Blob | null = null;
      for (let pass = 0; pass < MAX_RESIZE_PASSES; pass += 1) {
        canvas.width = dimensions.width;
        canvas.height = dimensions.height;
        context.fillStyle = "#fff";
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image.source, 0, 0, canvas.width, canvas.height);

        let passSmallest: Blob | null = null;
        for (const quality of JPEG_QUALITIES) {
          const candidate = await canvasBlob(canvas, quality);
          if (!smallest || candidate.size < smallest.size) smallest = candidate;
          if (!passSmallest || candidate.size < passSmallest.size) passSmallest = candidate;
          if (candidate.size <= TARGET_IMAGE_BYTES) break;
        }
        if (smallest && smallest.size <= TARGET_IMAGE_BYTES) break;
        if (!passSmallest) break;
        dimensions = reducedImageDimensions(dimensions, passSmallest.size);
      }
      if (!smallest || smallest.size >= file.size) return file;

      const stem = file.name.replace(/\.[^.]+$/, "") || "meal-photo";
      return new File([smallest], `${stem}.jpg`, { type: "image/jpeg", lastModified: file.lastModified });
    } finally {
      image.dispose();
    }
  } catch {
    // Compression is an optimization. A browser decode/canvas failure should
    // never prevent a supported original image from being uploaded.
    return file;
  }
};

export const imageUploadLimits = { maxEdge: MAX_IMAGE_EDGE, targetBytes: TARGET_IMAGE_BYTES } as const;
